/**
 * 검색 결과 표 하나가 XBRL 공시본에서 어떻게 매핑됐는지 — 대사 화면과 같은 읽기 전용 비교 뷰.
 * 결과 목록에서 "XBRL 매핑 보기"가 새 탭으로 연다(URL 쿼리로 표 id 를 받는다).
 */

import type { ReferenceTableId } from "@/apis/reference-search/types";
import {
  buildReadonlyCompareSection,
  ReadonlyCompareSection,
} from "@/components/readonly-compare";
import { referenceSearchQueries } from "@/queries";
import {
  createIxdDsdCompareStore,
  IxdDsdCompareStoreContext,
} from "@/stores/ixd-dsd-compare";
import { buildCellIdentityIndex } from "@/utils/ixd-cell-model";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { MAPPING_STATUS_LABEL } from "./mapping-status";

const EMPTY_LABEL_MAP = new Map<string, string>();

const readTableId = (params: URLSearchParams): ReferenceTableId | null => {
  const rceptNo = params.get("rceptNo");
  const attachmentCode = params.get("attachmentCode");
  const noteId = params.get("noteId");
  const subIndex = Number(params.get("subIndex"));
  if (!rceptNo || !attachmentCode || !noteId || Number.isNaN(subIndex))
    return null;
  return { rceptNo, attachmentCode, noteId, subIndex };
};

export const ReferenceMappingViewPage = () => {
  const [searchParams] = useSearchParams();
  const tableId = useMemo(() => readTableId(searchParams), [searchParams]);
  // 라이브 비교 화면과 같은 스토어 컨텍스트(IXD 캐러셀 상태). 페이지마다 인스턴스 하나.
  const storeRef = useRef<ReturnType<typeof createIxdDsdCompareStore> | null>(
    null,
  );
  if (!storeRef.current) storeRef.current = createIxdDsdCompareStore();

  const { data, isLoading, isError } = useQuery({
    ...referenceSearchQueries.xbrlMapping(
      tableId ?? { rceptNo: "", attachmentCode: "", noteId: "", subIndex: 0 },
    ),
    enabled: tableId !== null,
  });

  const section = useMemo(
    () => (data?.detail ? buildReadonlyCompareSection(data.detail) : null),
    [data],
  );
  const identityIndex = useMemo(
    () => buildCellIdentityIndex(data?.detail?.ixdChunks ?? []),
    [data],
  );
  const pendingFocus = useRef(null);

  if (!tableId)
    return (
      <Message text="잘못된 주소입니다. 검색 결과에서 다시 열어 주세요." />
    );
  if (isLoading) return <Message text="XBRL 매핑을 불러오는 중…" spinner />;
  if (isError || !data)
    return <Message text="XBRL 매핑을 불러오지 못했습니다." />;
  if (!section) {
    return (
      <Message
        text={
          MAPPING_STATUS_LABEL[data.status] ??
          "이 표의 XBRL 매핑 정보가 없습니다."
        }
      />
    );
  }

  return (
    <IxdDsdCompareStoreContext.Provider value={storeRef.current}>
      <div className="flex h-screen flex-col bg-background-neutral-alternative">
        <header className="flex items-center gap-3 border-b border-line-normal bg-white px-5 py-3">
          <h1 className="label-1-normal-bold text-label-normal">
            {section.title}
          </h1>
          <span className="caption-1-normal-medium text-label-assistive">
            ① DSD 원문 표 ↔ ② XBRL 공시 표. 셀을 클릭하면 대응 XBRL 셀로
            이동합니다. 색이 없는 셀은 매핑 근거를 찾지 못한 것입니다. 추론된
            DL·기간·항목·멤버 배정은 아래 스위치로 봅니다.
          </span>
        </header>
        <ReadonlyCompareSection
          section={section}
          identityIndex={identityIndex}
          ixdLabelMap={EMPTY_LABEL_MAP}
          onFocusLocation={() => false}
          pendingFocus={pendingFocus}
          regionReview
        />
      </div>
    </IxdDsdCompareStoreContext.Provider>
  );
};

const Message = ({
  text,
  spinner = false,
}: {
  text: string;
  spinner?: boolean;
}) => (
  <div className="flex h-screen items-center justify-center gap-2 body-1-normal-medium text-label-neutral">
    {spinner && <Loader2 className="size-4 animate-spin" />}
    {text}
  </div>
);
